import { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { Button } from "@/components/Button";
import { PasswordInput } from "@/components/PasswordInput";
import { ApiError, memberApi } from "@/lib/api";
import { useLogin, useMemberToken } from "@/hooks/useMemberAuth";
import type { TokenResponse } from "@/types/api";

function safeNext(raw: string | null): string {
  // Nur interne Pfade akzeptieren — vermeidet Open-Redirect.
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return "/konto/buchungen";
  return raw;
}

const schema = z.object({
  email: z.string().email("Gültige Email erforderlich"),
  password: z.string().min(1, "Passwort erforderlich"),
});
type Values = z.infer<typeof schema>;

const labelCls = "block text-body-sm font-medium text-ink";
const fieldCls =
  "mt-1 block w-full rounded border border-line bg-surface px-3 py-2 text-body-sm text-ink shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/30 focus:outline-none";
const errCls = "mt-1 text-caption text-danger";

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const token = useMemberToken();
  const login = useLogin();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    defaultValues: { email: "", password: "" },
  });

  const mutation = useMutation<TokenResponse, ApiError, Values>({
    mutationFn: (v) => memberApi.login(schema.parse(v)),
    onSuccess: (data) => {
      login(data.access_token);
      navigate(next);
    },
  });

  useEffect(() => {
    if (token) navigate(next, { replace: true });
  }, [token, navigate, next]);

  return (
    <div className="mx-auto max-w-md px-6 py-12">
      <h1 className="mb-2 font-display text-h1 uppercase text-primary">Anmelden</h1>
      <p className="mb-8 text-body text-ink-soft">
        Melde dich mit deiner Email und Passwort an, um deine Buchungen zu sehen. Der Login ist
        ausschließlich für ausgewählte, mit der Tanzschule Tanzraum assoziierte Tanzlehrer:innen.
      </p>

      <form
        onSubmit={handleSubmit((v) => mutation.mutate(v))}
        className="space-y-5"
        noValidate
      >
        <div>
          <label htmlFor="email" className={labelCls}>
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            {...register("email")}
            className={fieldCls}
          />
          {errors.email && <p className={errCls}>{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="password" className={labelCls}>
            Passwort
          </label>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            invalid={!!errors.password}
            {...register("password")}
            className="mt-1"
          />
          {errors.password && <p className={errCls}>{errors.password.message}</p>}
        </div>

        {mutation.error && (
          <div className="rounded-card border border-danger bg-danger/5 p-3 text-body-sm text-danger">
            {mutation.error.message}
          </div>
        )}

        <Button type="submit" size="lg" loading={isSubmitting || mutation.isPending}>
          Einloggen
        </Button>

        <p className="text-caption text-ink-muted">
          Noch kein Konto?{" "}
          <Link to="/konto/registrieren" className="text-primary underline">
            Jetzt registrieren
          </Link>
          .
        </p>
      </form>
    </div>
  );
}
